import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, MessageCircle, Phone, Send, Siren, X } from "lucide-react";
import { BRAND } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const EMERGENCY_LINKS = [
  {
    id: "call",
    label: "Sofort anrufen",
    sub: BRAND.phone,
    href: BRAND.phoneHref,
    icon: Phone,
    cls: "bg-[#D81E2F] text-white",
  },
  {
    id: "whatsapp",
    label: "WhatsApp senden",
    sub: "Standort & Fläche schicken",
    href: BRAND.whatsapp,
    icon: MessageCircle,
    external: true,
    cls: "bg-[#25D366] text-white",
  },
  {
    id: "email",
    label: "E-Mail schreiben",
    sub: BRAND.email,
    href: BRAND.emailHref,
    icon: Mail,
    cls: "bg-white text-[#062B52] border border-[#DCE5EF]",
  },
];

const MobileBar = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="mobile-emergency-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-[#062B52]/55 backdrop-blur-[2px] lg:hidden"
            />
            <motion.div
              data-testid="mobile-emergency-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.38, ease: EASE }}
              className="fixed inset-x-0 bottom-0 z-[61] overflow-hidden rounded-t-3xl bg-white shadow-[0_-30px_80px_-30px_rgba(6,43,82,0.65)] lg:hidden"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
            >
              <div className="relative overflow-hidden bg-[#D81E2F] px-5 pb-5 pt-4 text-white">
                <span className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-white/10" />
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                      </span>
                      <p className="font-mono-label text-[9.5px] text-white">Notruf 24/7 im Einsatz</p>
                    </div>
                    <p className="mt-2 text-[15px] font-bold leading-snug text-white">
                      Schnee oder Glätte akut? Direkt melden.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-emergency-close"
                    aria-label="Notruf schließen"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white"
                  >
                    <X className="h-[18px] w-[18px]" />
                  </button>
                </div>
              </div>

              <div className="p-4">
                <p className="text-[11.5px] leading-relaxed text-[#66788A]">
                  Bereitschaft für betreute Objekte in Bremen – Einsätze erfolgen nach
                  vereinbarten Prioritäten.
                </p>
                <div className="mt-3.5 grid gap-2">
                  {EMERGENCY_LINKS.map((l) => (
                    <a
                      key={l.id}
                      href={l.href}
                      data-testid={`mobile-emergency-${l.id}`}
                      {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-3 rounded-xl px-3.5 py-3 ${l.cls}`}
                    >
                      <l.icon className="h-4 w-4 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold leading-tight">{l.label}</span>
                        <span
                          className={`mt-0.5 block truncate text-[11px] leading-tight ${
                            l.id === "email" ? "text-[#66788A]" : "text-white/85"
                          }`}
                        >
                          {l.sub}
                        </span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div
        data-testid="mobile-bottom-bar"
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-[#DCE5EF] bg-white/95 backdrop-blur-xl lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="bottombar-emergency"
          aria-expanded={open}
          aria-label="Notruf 24/7 – Kontaktmöglichkeiten öffnen"
          className="relative flex flex-col items-center justify-center gap-1 bg-[#D81E2F] py-2.5 text-[11px] font-semibold text-white"
        >
          <span className="relative">
            <Siren className="h-[18px] w-[18px]" />
            <span className="absolute -right-1 -top-1 flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
            </span>
          </span>
          Notruf
        </button>
        <a
          href={BRAND.phoneHref}
          data-testid="bottombar-call"
          className="flex flex-col items-center justify-center gap-1 border-r border-[#DCE5EF] py-2.5 text-[11px] font-semibold text-[#0757B8]"
        >
          <Phone className="h-[18px] w-[18px]" />
          Anrufen
        </a>
        <a
          href={BRAND.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="bottombar-whatsapp"
          className="flex flex-col items-center justify-center gap-1 border-r border-[#DCE5EF] py-2.5 text-[11px] font-semibold text-[#1fae56]"
        >
          <MessageCircle className="h-[18px] w-[18px]" />
          WhatsApp
        </a>
        <Link
          to="/kontakt"
          data-testid="bottombar-request"
          className="flex flex-col items-center justify-center gap-1 bg-[#FF6B00] py-2.5 text-[11px] font-semibold text-white"
        >
          <Send className="h-[18px] w-[18px]" />
          Anfrage
        </Link>
      </div>
    </>
  );
};

export default MobileBar;
